<script setup lang="ts">
import { ref } from "vue";

import {
  HOW_IT_WORKS_LINK,
  MWS_HOMEPAGE_LINK,
  SHIPPING_DELIVERY_LINK,
} from "@/constants/categoriesLinks";

const { clubName } = defineProps<{
  clubName: string;
}>();

const localePath = useLocalePath();

const containerExpandedHeight = "max-h-[5000px]";
const containerCollapsedHeight = "max-h-[220px] collapsed";
const isExpanded = ref(false);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <section
    class="block-viewport-container site-viewport-container mt-6 mb-8 relative"
  >
    <div
      class="main-content transition-all duration-200 overflow-hidden"
      :class="[isExpanded ? containerExpandedHeight : containerCollapsedHeight]"
    >
      <div class="mb-6">
        <h2 class="flex font-bold uppercase text-2xl mb-2">
          {{ $t("category.staticContent.shirts", { clubName }) }}
        </h2>
        <p>
          {{ $t("category.staticContent.shirtsContent", { clubName }) }}
        </p>
      </div>

      <div class="mb-6">
        <h2 class="flex font-bold uppercase text-2xl mb-2">
          {{ $t("category.staticContent.shirtsSpecs", { clubName }) }}
        </h2>
        <p>
          {{ $t("category.staticContent.shirtsSpecsContent", { clubName }) }}
        </p>

        <ul class="list-disc ml-8">
          <li>
            {{ $t("category.staticContent.shirtsSpecsContentListItem1") }}
          </li>
          <li>
            {{ $t("category.staticContent.shirtsSpecsContentListItem2") }}
          </li>
          <li>
            {{ $t("category.staticContent.shirtsSpecsContentListItem3") }}
          </li>
        </ul>

        <p class="mt-2">
          {{ $t("category.staticContent.shirtsSpecsNote") }}
        </p>
      </div>

      <div class="mb-6">
        <h2 class="flex font-bold uppercase text-2xl mb-2">
          {{ $t("category.staticContent.shirtsDelivery") }}
        </h2>
        <p>
          {{ $t("category.staticContent.shirtsDeliveryContent") }}
        </p>

        <ul class="list-disc ml-8">
          <li>
            {{ $t("category.staticContent.shirtsDeliveryContentListItem1") }}
          </li>
          <li>
            {{ $t("category.staticContent.shirtsDeliveryContentListItem2") }}
          </li>
          <li>
            {{ $t("category.staticContent.shirtsDeliveryContentListItem3") }}
          </li>
        </ul>

        <I18nT
          scope="global"
          keypath="category.staticContent.shirtsOrders"
          class="mt-2"
          tag="p"
        >
          <template #PLACEHOLDER_shirtFAQ>
            <NuxtLink :to="SHIPPING_DELIVERY_LINK">
              {{ $t("category.staticContent.shirtsFAQ") }}
            </NuxtLink>
          </template>
        </I18nT>
      </div>

      <div class="mb-6">
        <h2 class="flex font-bold uppercase text-2xl mb-2">
          {{ $t("category.staticContent.shirtsWhyBuy") }}
        </h2>
        <p>
          {{ $t("category.staticContent.shirtsWhyBuyContent") }}
        </p>

        <ul class="list-disc ml-8 mt-2">
          <li>
            {{ $t("category.staticContent.shirtsWhyBuyContentListItem1") }}
          </li>
          <li>
            {{ $t("category.staticContent.shirtsWhyBuyContentListItem2") }}
          </li>
          <li>
            {{ $t("category.staticContent.shirtsWhyBuyContentListItem3") }}
          </li>
        </ul>

        <I18nT
          scope="global"
          keypath="category.staticContent.shirtsWhyBuyContentNote"
          class="mt-2"
          tag="p"
        >
          <template #PLACEHOLDER_shirtHowItWorksLink>
            <NuxtLink :to="localePath(HOW_IT_WORKS_LINK)">
              {{ $t("category.staticContent.shirtsWhyBuyContentHowItWorks") }}
            </NuxtLink>
          </template>
          <template #PLACEHOLDER_shirtsHomePageLink>
            <NuxtLink :to="localePath(MWS_HOMEPAGE_LINK)">
              {{ $t("category.staticContent.shirtsWhyBuyContentHomepage") }}
            </NuxtLink>
          </template>
        </I18nT>
      </div>

      <h2 class="flex font-bold uppercase text-2xl mb-2">
        {{ $t("category.staticContent.shirtsAllAboutClub", { clubName }) }}
      </h2>

      <div class="mb-5">
        <h3 class="flex font-bold uppercase text-lg">
          {{ $t("category.staticContent.shirtsAllAboutClubQuestion1") }}
        </h3>
        <p>
          {{ $t("category.staticContent.shirtsAllAboutClubAnswer1") }}
        </p>
      </div>

      <div class="mb-5">
        <h3 class="flex font-bold uppercase text-lg">
          {{
            $t("category.staticContent.shirtsAllAboutClubQuestion2", {
              clubName,
            })
          }}
        </h3>
        <p>
          {{ $t("category.staticContent.shirtsAllAboutClubAnswer2") }}
        </p>
      </div>

      <div class="mb-5">
        <h3 class="flex font-bold uppercase text-lg">
          {{
            $t("category.staticContent.shirtsAllAboutClubQuestion3", {
              clubName,
            })
          }}
        </h3>
        <p>
          {{ $t("category.staticContent.shirtsAllAboutClubAnswer3") }}
        </p>
      </div>

      <div class="mb-5">
        <h3 class="flex font-bold uppercase text-lg">
          {{
            $t("category.staticContent.shirtsAllAboutClubQuestion4", {
              clubName,
            })
          }}
        </h3>
        <p>
          {{ $t("category.staticContent.shirtsAllAboutClubAnswer4") }}
        </p>
      </div>
    </div>

    <BaseButton
      class="mt-4"
      color-scheme="floating"
      @click="toggleExpanded"
    >
      {{
        isExpanded
          ? $t("category.staticContent.shirtsShowLess")
          : $t("category.staticContent.shirtsShowMore")
      }}
    </BaseButton>
  </section>
</template>

<style lang="postcss" scoped>
.main-content.collapsed::after {
  content: "";
  position: absolute;
  bottom: 20px;
  width: 100%;
  height: 80px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 1) 95%
  );
  pointer-events: none;
}
</style>
